import React, { useState, useMemo } from 'react';
import { LineChart, Line, ResponsiveContainer, Tooltip } from 'recharts';
import Icon from 'components/AppIcon';

export default function EurUsdFeedPanel({ ticks, onSaveOverride }) {
  const [overrideMode, setOverrideMode] = useState(false);
  const [overrideValue, setOverrideValue] = useState('');
  const [saving, setSaving] = useState(false);

  const eurUsdTicks = useMemo(() =>
    ticks?.filter(t => t?.symbol === 'EUR_USD')?.sort((a, b) => new Date(b?.ts) - new Date(a?.ts)) || []
  , [ticks]);

  const latest = eurUsdTicks?.[0];
  const currentRate = latest ? parseFloat(latest?.value) : null;

  const last30 = eurUsdTicks?.slice(0, 30);

  const sparkData = useMemo(() => {
    const cutoff = new Date(Date.now() - 24 * 60 * 60 * 1000);
    return eurUsdTicks?.filter(t => new Date(t.ts) >= cutoff)?.sort((a, b) => new Date(a.ts) - new Date(b.ts))?.map(t => ({ ts: t?.ts, value: parseFloat(t?.value) }));
  }, [eurUsdTicks]);

  const handleSave = async () => {
    const val = parseFloat(overrideValue);
    if (isNaN(val) || val <= 0) return;
    setSaving(true);
    await onSaveOverride?.(val);
    setSaving(false);
    setOverrideMode(false);
    setOverrideValue('');
  };

  return (
    <div className="bg-card border border-border rounded-md p-4" style={{ boxShadow: 'var(--shadow-sm)' }}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Icon name="DollarSign" size={16} color="var(--color-accent)" />
          <h3 className="text-sm font-semibold text-foreground font-caption uppercase tracking-wider">EUR/USD Feed</h3>
        </div>
        <button
          onClick={() => setOverrideMode(v => !v)}
          className={`flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-semibold transition-all ${
            overrideMode ? 'bg-accent text-white' : 'bg-muted text-muted-foreground hover:text-foreground'
          }`}
        >
          <Icon name="Edit2" size={12} color="currentColor" />
          Override
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Left: Rate + Override */}
        <div className="space-y-3">
          <div className="flex items-end gap-2">
            <span className="text-3xl font-bold text-foreground font-data">
              {currentRate ? currentRate?.toFixed(4) : '—'}
            </span>
            <span className="text-sm text-muted-foreground mb-1">EUR/USD</span>
          </div>
          {latest && (
            <div className="text-xs text-muted-foreground font-data">
              Actualizado: {new Date(latest.ts)?.toLocaleString('es-ES', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
              <span className="ml-2 px-1.5 py-0.5 rounded text-xs" style={{ background: 'var(--color-muted)', color: 'var(--color-muted-foreground)' }}>
                {latest?.source}
              </span>
            </div>
          )}

          {overrideMode && (
            <div className="flex gap-2 items-center p-3 rounded-md" style={{ background: 'var(--color-muted)' }}>
              <input
                type="number"
                step="0.0001"
                placeholder="1.0850"
                value={overrideValue}
                onChange={e => setOverrideValue(e?.target?.value)}
                className="flex-1 text-sm border border-border rounded px-2 py-1.5 bg-background text-foreground font-data"
              />
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-3 py-1.5 bg-accent text-white rounded text-xs font-semibold disabled:opacity-50"
              >
                {saving ? '...' : 'Guardar'}
              </button>
            </div>
          )}

          {/* Sparkline */}
          {sparkData?.length > 1 && (
            <div>
              <div className="text-xs text-muted-foreground mb-1 font-caption">Últimas 24h</div>
              <div style={{ height: 60 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={sparkData}>
                    <Line type="monotone" dataKey="value" stroke="var(--color-accent)" strokeWidth={1.5} dot={false} />
                    <Tooltip
                      contentStyle={{ background: 'var(--color-card)', border: '1px solid var(--color-border)', borderRadius: 6, fontSize: 11 }}
                      formatter={v => [v?.toFixed(4), 'EUR/USD']}
                      labelFormatter={l => new Date(l)?.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </div>

        {/* Right: Last 30 ticks table */}
        <div>
          <div className="text-xs text-muted-foreground font-caption mb-2">Últimas 30 entradas</div>
          <div className="overflow-y-auto" style={{ maxHeight: 200 }}>
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-1 text-muted-foreground font-caption">Hora</th>
                  <th className="text-right py-1 text-muted-foreground font-caption">Valor</th>
                  <th className="text-right py-1 text-muted-foreground font-caption">Fuente</th>
                </tr>
              </thead>
              <tbody>
                {last30?.length === 0 ? (
                  <tr><td colSpan={3} className="text-center py-4 text-muted-foreground">Sin datos EUR/USD</td></tr>
                ) : (
                  last30?.map(t => (
                    <tr key={t?.id} className="border-b border-border/50 hover:bg-muted/50">
                      <td className="py-1 text-muted-foreground font-data">
                        {new Date(t.ts)?.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                      </td>
                      <td className="py-1 text-right text-foreground font-data font-medium">{parseFloat(t?.value)?.toFixed(4)}</td>
                      <td className="py-1 text-right">
                        <span className="px-1.5 py-0.5 rounded text-xs font-caption"
                          style={{ background: 'var(--color-muted)', color: 'var(--color-muted-foreground)' }}>
                          {t?.source}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
